import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Input, Combobox, Card, Modal, Table, Pagination, Badge, NumberInput } from './common';
import { apiError } from '../services/api';
import { useAuth } from '../context/AuthContext';

const PAGE_SIZE = 10;

function emptyForm(fields) {
  const f = {};
  fields.forEach((fld) => {
    f[fld.key] = fld.default ?? '';
  });
  return f;
}

/**
 * Generic master-data page: searchable list + create/edit modal + delete
 * confirmation. Configured per resource via props.
 *
 * fields: [{ key, label, type: 'text'|'number'|'decimal'|'select'|'textarea',
 *            required, options?, lookup?, immutableOnEdit?, step?, placeholder?,
 *            fullWidth? }]  (textarea is always full width in a 2-column form)
 * formColumns: 1 (default) or 2 — lays the form fields out in a 2-column grid.
 * lookups: { <lookupName>: [{ value, label }] }
 */
export default function CrudPage({
  title,
  service,
  formCode,
  columns,
  fields,
  lookups = {},
  searchable = true,
  searchKeys,
  rowActions,
  headerActions,
  renderFormExtra,
  formColumns = 1,
  onToast,
}) {
  const { can } = useAuth();
  // When no formCode is given, treat all actions as allowed (permission-agnostic pages).
  const canView = !formCode || can(formCode, 'CanView');
  const canCreate = !formCode || can(formCode, 'CanCreate');
  const canEdit = !formCode || can(formCode, 'CanEdit');
  const canDelete = !formCode || can(formCode, 'CanDelete');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm(fields));
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Load the full list once; filtering happens client-side (see `filtered`).
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await service.list();
      setRows(data);
    } catch (err) {
      onToast?.(apiError(err).message, 'error');
    } finally {
      setLoading(false);
    }
  }, [service, onToast]);

  useEffect(() => {
    load();
  }, [load]);

  // Client-side instant filter. Matches against searchKeys (or all row values).
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) => {
      const keys = searchKeys || Object.keys(row);
      return keys.some((k) => String(row[k] ?? '').toLowerCase().includes(term));
    });
  }, [rows, search, searchKeys]);

  const resolveOptions = (fld) => fld.options || lookups[fld.lookup] || [];

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm(fields));
    setFieldErrors({});
    setSubmitError('');
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    const f = {};
    fields.forEach((fld) => {
      f[fld.key] = row[fld.key] ?? '';
    });
    setForm(f);
    setFieldErrors({});
    setSubmitError('');
    setModalOpen(true);
  };

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  // Unified field change: applies uppercase, updates the value, and clears this
  // field's error the moment it becomes valid (live validation, not just on submit).
  const changeField = (fld, value) => {
    const v = fld.uppercase && typeof value === 'string' ? value.toUpperCase() : value;
    setField(fld.key, v);
    setFieldErrors((prev) => {
      if (!prev[fld.key]) return prev;
      const valid = !fld.required || (v !== '' && v !== null && v !== undefined);
      if (!valid) return prev;
      const next = { ...prev };
      delete next[fld.key];
      return next;
    });
  };

  const validate = () => {
    const errs = {};
    fields.forEach((fld) => {
      if (editing && fld.immutableOnEdit) return;
      const v = form[fld.key];
      if (fld.required && (v === '' || v === null || v === undefined)) {
        errs[fld.key] = `${fld.label} is required`;
      }
    });
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const buildPayload = () => {
    const payload = {};
    fields.forEach((fld) => {
      if (editing && fld.immutableOnEdit) return; // locked field: don't send
      let v = form[fld.key];
      if (v === '') v = null;
      if ((fld.type === 'number' || fld.type === 'decimal' || fld.type === 'select') && v !== null) {
        // FK selects and numeric fields go over the wire as numbers.
        const n = Number(v);
        v = Number.isNaN(n) ? v : n;
      }
      payload[fld.key] = v;
    });
    return payload;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setSubmitError('');
    try {
      const payload = buildPayload();
      if (editing) {
        await service.update(editing.id, payload);
        onToast?.(`${title} updated`);
      } else {
        await service.create(payload);
        onToast?.(`${title} created`);
      }
      setModalOpen(false);
      load();
    } catch (err) {
      setSubmitError(apiError(err).message);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    setDeleteError('');
    try {
      await service.remove(deleteTarget.id);
      onToast?.(`${title} deleted`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      const e = apiError(err);
      // 409 => referenced / in use
      setDeleteError(e.message);
    } finally {
      setDeleting(false);
    }
  };

  const allColumns = useMemo(
    () => [
      ...columns,
      {
        key: '__actions',
        label: 'Actions',
        className: 'text-right whitespace-nowrap',
        render: (row) => (
          <div className="flex justify-end gap-1.5">
            {rowActions?.(row, { reload: () => load() })}
            {canEdit && (
              <Button size="sm" variant="secondary" onClick={() => openEdit(row)}>
                Edit
              </Button>
            )}
            {canDelete && (
              <Button
                size="sm"
                variant="danger"
                onClick={() => {
                  setDeleteError('');
                  setDeleteTarget(row);
                }}
              >
                Delete
              </Button>
            )}
          </div>
        ),
      },
    ],
    [columns, rowActions, search, canEdit, canDelete] // eslint-disable-line
  );

  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (!canView) {
    return (
      <Card title={title}>
        <p className="py-6 text-center text-sm text-gray-500">
          You don’t have permission to view this page.
        </p>
      </Card>
    );
  }

  return (
    <Card
      title={title}
      actions={
        (headerActions || canCreate) ? (
          <div className="flex gap-2">
            {headerActions}
            {canCreate && <Button onClick={openCreate}>+ New</Button>}
          </div>
        ) : null
      }
    >
      {searchable && (
        <div className="mb-4 flex gap-2">
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search…"
            className="w-64 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
          />
          {search && (
            <Button type="button" variant="ghost" onClick={() => setSearch('')}>
              Clear
            </Button>
          )}
        </div>
      )}

      <Table columns={allColumns} rows={pageRows} loading={loading} />
      <Pagination page={page} pageSize={PAGE_SIZE} totalItems={filtered.length} onPageChange={setPage} />

      {/* Create / Edit modal */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={`${editing ? 'Edit' : 'New'} ${title}`}
        size={formColumns > 1 ? 'lg' : 'md'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      >
        <form
          onSubmit={submit}
          className={formColumns > 1 ? 'grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2' : 'space-y-3'}
        >
          {submitError && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 sm:col-span-full">
              {submitError}
            </div>
          )}
          {fields.map((fld) => (
            <div
              key={fld.key}
              className={
                formColumns > 1 && (fld.fullWidth || fld.type === 'textarea') ? 'sm:col-span-full' : ''
              }
            >
              {((fld) => {
            const locked = editing && fld.immutableOnEdit;
            if (fld.type === 'select') {
              return (
                <Combobox
                  key={fld.key}
                  label={fld.label}
                  required={fld.required}
                  placeholder={fld.placeholder ?? '-- select --'}
                  options={resolveOptions(fld)}
                  value={form[fld.key] ?? ''}
                  disabled={locked}
                  error={fieldErrors[fld.key]}
                  onChange={(val) => changeField(fld, val)}
                />
              );
            }
            if (fld.type === 'textarea') {
              // Fixed height, non-resizable, scrolls when content overflows.
              return (
                <label key={fld.key} className="block">
                  <span className="mb-1 block text-sm font-medium text-gray-700">{fld.label}</span>
                  <textarea
                    value={form[fld.key] ?? ''}
                    disabled={locked}
                    onChange={(e) => changeField(fld, e.target.value)}
                    style={{ resize: 'none' }}
                    className="h-20 w-full overflow-y-auto rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:bg-gray-100"
                  />
                </label>
              );
            }
            if (fld.type === 'number' || fld.type === 'decimal') {
              return (
                <NumberInput
                  key={fld.key}
                  label={fld.label + (locked ? ' (locked)' : '')}
                  required={fld.required}
                  allowDecimal={fld.type === 'decimal'}
                  placeholder={fld.placeholder}
                  value={form[fld.key] ?? ''}
                  disabled={locked}
                  error={fieldErrors[fld.key]}
                  onChange={(raw) => changeField(fld, raw)}
                />
              );
            }
            return (
              <Input
                key={fld.key}
                label={fld.label + (locked ? ' (locked)' : '')}
                required={fld.required}
                type="text"
                placeholder={fld.placeholder}
                value={form[fld.key] ?? ''}
                disabled={locked}
                error={fieldErrors[fld.key]}
                onChange={(e) => changeField(fld, e.target.value)}
              />
            );
              })(fld)}
            </div>
          ))}
          {renderFormExtra && (
            <div className="sm:col-span-full">
              {renderFormExtra({ editing, reload: () => load(search) })}
            </div>
          )}
        </form>
      </Modal>

      {/* Delete confirmation modal */}
      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={`Delete ${title}`}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDelete} disabled={deleting || !!deleteError}>
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </>
        }
      >
        {deleteError ? (
          <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            <Badge tone="red">Cannot delete</Badge>
            <p className="mt-2">{deleteError}</p>
          </div>
        ) : (
          <p className="text-sm text-gray-600">This will be permanently deleted. Continue?</p>
        )}
      </Modal>
    </Card>
  );
}
